import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { DataContext } from '../../../context/DataContext';
import { getRol, getToken, getUser } from '../../../api/Account';
import { getRecuperacionVencidosAsync } from '../../../api/Report';
import { getGestoresAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { formatearFecha, formatearMonto } from '../../../helpers/formato';
import ReporteAgrupado from '../../../components/reportes/ReporteAgrupado';

const TODOS = 't';

// Mismos valores que RecuperacionVencidos.Estado* en el servidor.
const NO_ABONADOS = 2;
const NOMBRE_ESTADO = { 0: 'Todos', 1: 'Abonados', 2: 'No abonados' };

// Columna de dinero. Los montos y saldos son del crédito: si a un crédito le cobraron
// dos usuarios en el rango sale una fila con cada uno, y en el total cuentan una vez.
// Los montos del préstamo no se totalizan (pedido del usuario): solo saldos y recuperado.
const dinero = (key, label, delCredito = false, total = true) => ({
	key,
	label,
	align: 'right',
	total,
	unaVezPorId: delCredito,
	formatoTotal: formatearMonto,
	valor: (c) => formatearMonto(c[key]),
	excel: (c) => c[key]
});

const COLUMNAS_CREDITO = [
	{ key: 'id', label: '#', align: 'center', valor: (c) => c.id, excel: (c) => c.id },
	{ key: 'fullName', label: 'Cliente', align: 'left', texto: true, valor: (c) => c.fullName },
	{
		key: 'fechas',
		label: 'Fecha E/V',
		align: 'center',
		orden: 'fechaVencimiento',
		valor: (c) => (
			<Stack sx={{ whiteSpace: 'nowrap' }}>
				<span>{`E: ${formatearFecha(c.fechaEntrega)}`}</span>
				<span>{`V: ${formatearFecha(c.fechaVencimiento)}`}</span>
			</Stack>
		),
		excel: (c) =>
			`${formatearFecha(c.fechaEntrega)} / ${formatearFecha(c.fechaVencimiento)}`
	},
	{
		key: 'diasVencidos',
		label: 'Días Venc.',
		align: 'center',
		valor: (c) => c.diasVencidos,
		excel: (c) => c.diasVencidos
	},
	dinero('montoEntregado', 'Entregado', true, false),
	dinero('montoCapital', 'Capital', true, false),
	dinero('montoInteres', 'Interés', true, false),
	dinero('montoPagar', 'A Pagar', true, false),
	dinero('saldoCapital', 'Saldo C.', true),
	dinero('saldoIC', 'Saldo IC.', true),
	dinero('saldoTotal', 'Saldo T.', true)
];

// Lo abonado en el rango por el gestor de la fila (quien cobró).
const COLUMNAS_RECUPERACION = [
	{
		key: 'ultimoAbono',
		label: 'Últ. Abono',
		align: 'center',
		valor: (c) =>
			c.ultimoAbono
				? `${formatearFecha(c.ultimoAbono)}${
						c.cantidadAbonos > 1 ? ` (${c.cantidadAbonos})` : ''
				  }`
				: '—',
		excel: (c) => (c.ultimoAbono ? formatearFecha(c.ultimoAbono) : '')
	},
	dinero('recuperadoCapital', 'Rec. C.'),
	dinero('recuperadoInteres', 'Rec. IC.'),
	dinero('recuperadoTotal', 'Recuperado')
];

const RecuperacionVencidos = () => {
	const [data, setData] = useState([]);
	const [gestorName, setGestorName] = useState('Todos');

	const { selectedGestor, selectedSortBy, fechaDesde, fechaHasta, estado } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			const [reporte, gestores] = await Promise.all([
				getRecuperacionVencidosAsync(token, {
					selectedSortBy: parseInt(selectedSortBy),
					gestorId: selectedGestor === TODOS ? 0 : parseInt(selectedGestor),
					desde: fechaDesde,
					hasta: fechaHasta,
					estado: parseInt(estado)
				}),
				selectedGestor === TODOS ? null : getGestoresAsync(token)
			]);
			setIsLoading(false);

			if (!reporte.statusResponse) {
				manejarError(reporte);
				return;
			}
			setData(reporte.data.result);

			if (gestores && gestores.statusResponse) {
				const gestor = gestores.data.result.find(
					(g) => g.id.toString() === selectedGestor
				);
				setGestorName(gestor ? gestor.fullName : selectedGestor);
			}
		})();
	}, [
		token,
		selectedGestor,
		selectedSortBy,
		fechaDesde,
		fechaHasta,
		estado,
		setIsLoading,
		manejarError
	]);

	// Sin abonos en el rango, las columnas de recuperación saldrían todas en cero.
	const columnas =
		parseInt(estado) === NO_ABONADOS
			? COLUMNAS_CREDITO
			: [...COLUMNAS_CREDITO, ...COLUMNAS_RECUPERACION];

	// El servidor ya manda el nombre: también el de un gestor dado de baja.
	const agruparPor = useCallback((fila) => fila.gestorNombre, []);

	return (
		<ReporteAgrupado
			tituloReporte="Reporte de Recuperación de Créditos Vencidos"
			nombreArchivo="RecuperacionCreditosVencidos"
			parametros={[
				{ label: 'Gestor', valor: gestorName },
				{ label: 'Créditos', valor: NOMBRE_ESTADO[estado] || 'Todos' },
				{
					label: 'Recuperación',
					valor: `del ${moment(fechaDesde).format('DD/MM/YYYY')} al ${moment(
						fechaHasta
					).format('DD/MM/YYYY')}`
				}
			]}
			columnas={columnas}
			data={data}
			agruparPor={agruparPor}
			ordenInicial="fullName"
			contexto={{ title, slogan, icon, user, rol }}
		/>
	);
};

export default RecuperacionVencidos;
